import Main from '../layouts/Main';
import { Accordion, AccordionDetails, AccordionSummary, Button, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from 'next/link';
import Head from 'next/head';
import { CANONICAL_DOMAIN } from '../utils/constants';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

export default function WhatToExpect() {
  const [openPanels, setOpenPanels] = useState({
    expect: false,
    wear: false,
    children: false,
  });

  const { asPath } = useRouter();

  const togglePanel = (panel: string) => {
    setOpenPanels((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  useEffect(() => {
    const hash = asPath.split('#')[1];

    if (hash) {
      togglePanel('children');
      setTimeout(() => {
        const div = document.getElementById('children');
        div.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }
  }, [asPath]);

  return (
    <Main header_title="We're Glad You're Here!">
      <Head>
        <title>Fosterburg Baptist Church | What to Expect</title>
        <meta property="og:title" content="Fosterburg Baptist Church | What to Expect" key="title" />
        <link rel="canonical" href={`${CANONICAL_DOMAIN}/what-to-expect`} />
      </Head>
      <div style={{ margin: 15, marginTop: 50 }}>
        <div style={{ textAlign: 'center', margin: '0 auto' }}>
          <Typography variant="h6">We&#39;d love to answer your questions and help you learn more about Fosterburg Baptist Church.</Typography>
        </div>
        <div style={{ margin: '0 auto', marginTop: 25, maxWidth: 1000 }}>
          <Accordion square={true} expanded={openPanels['expect']} onChange={() => togglePanel('expect')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="expect-content" id="expect">
              <Typography>What Should I Expect?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                If you are new to our church, the easiest way to find out who we are is to join us on a Sunday morning at 10 am.
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                Our Sunday worship services are weekly opportunities for every age group to pray together, sing, and study God&#39;s Word. We praise God through
                traditional and modern hymns.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion square={true} expanded={openPanels['wear']} onChange={() => togglePanel('wear')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="wear-content" id="wear">
              <Typography>What Should I Wear?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                We want you to feel comfortable while you&#39;re with us. Come as you are and wear what you would like.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion square={true} expanded={openPanels['children']} onChange={() => togglePanel('children')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="childcare-content" id="children">
              <Typography>What&#39;s Available for My Children?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                0 - 12 months
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                Nursery care is available on Sunday mornings, evenings, and Wednesday nights for 0-12 months in the upstairs nursery. Upon dropping off your
                child you will list any pertinent information the caregivers should be aware of and provide your cell phone number so that the caregivers may
                reach you if your child needs you for any reason. This nursery is supplied with a changing table, crib, swing, private nursing area, and age
                appropriate toys.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                1 year old
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                1 year old nursery is located downstairs and available on Sunday mornings, evenings, and Wednesday nights. Upon dropping off your child please
                make the caregivers aware of any pertinent information and provide your cell phone number so that the caregivers may reach you if your child
                needs you for any reason. This nursery is supplied with a changing table and age appropriate toys.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                2 and 3 year olds
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                2 - 3 year old nursery is located downstairs and available on Sunday mornings, evenings, and Wednesday nights. This age group is welcome to join
                their parents during the music portion of our 10am service or go downstairs for the entirety of the service. If you choose to keep your child
                with you they will be dismissed before the sermon begins and you can take them downstairs at that time. This age group joins our grade school
                age children for song time and then has a special lesson geared towards their age along with playtime. Please make the caregivers aware of any
                pertinent information and provide your cell phone number so that the caregivers may reach you if your child needs you for any reason.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                4yr - 2nd grade
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                Children&apos;s church is available for 4yr - 2nd graders during the 10am worship service. They will be dismissed before the sermon begins and
                you can feel free to walk them to the foyer where their teacher will be waiting. They gather downstairs to read the Bible, pray, sing together,
                and more! We are teaching the next generation how to worship our God all at their level!
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Pickup
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                At the conclusion of the 10am service, the 4yr - 2nd graders will be waiting in the foyer with their teachers. 0-3yr olds will be in the nursery
                and must be picked up by their caregiver.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Safety
              </Typography>
              <Typography paragraph variant="body2" style={{ textAlign: 'left' }}>
                All nursery and children&#39;s church staff are members of the church and have completed background checks. Members of the church with emergency
                response training are present should any need arise.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </div>
        <div style={{ textAlign: 'center', margin: '0 auto', marginTop: 35 }}>
          <Typography variant="h6">Still have questions?</Typography>
          <div style={{ marginTop: 15 }}>
            <Link href="/contact-us" passHref>
              <Button variant="outlined" color="secondary">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Main>
  );
}
